import { formatMoney } from '@/helpers/money';
import { cn } from '@/utils/cn';

export interface AmountDisplayProps {
  /** Decimal string in major units. A `number` is a compile error. */
  amount: string;
  currency: string;
  decimalPlaces?: number | undefined;
  /** Active locale, e.g. `en`. The primitive never reads i18n itself. */
  locale: string;
  size?: 'md' | 'lg' | 'xl';
  /** Already localized, e.g. "Amount to pay". */
  label?: string | undefined;
  className?: string;
}

const SIZES = {
  md: 'text-base',
  lg: 'text-xl',
  xl: 'text-2xl',
} as const;

/**
 * The amount is the largest text on a funnel screen. Formatting goes through `formatMoney` so a
 * `parseFloat` cannot sneak in at a call site.
 */
export function AmountDisplay({
  amount,
  currency,
  decimalPlaces = 2,
  locale,
  size = 'lg',
  label,
  className,
}: AmountDisplayProps) {
  const formatted = formatMoney(amount, currency, locale, decimalPlaces);

  return (
    <div className={cn('min-w-0', className)}>
      {label !== undefined && label !== '' && (
        <p className="text-text-muted text-xs font-medium">{label}</p>
      )}
      <p className={cn('text-text font-semibold tracking-tight break-all tabular-nums', SIZES[size])}>{formatted}</p>
    </div>
  );
}

export interface AmountComparisonProps {
  amount: string;
  receivedAmount: string;
  currency: string;
  decimalPlaces?: number | undefined;
  locale: string;
  /** Already localized. */
  requestedLabel: string;
  /** Already localized. */
  receivedLabel: string;
  className?: string;
}

/**
 * Both values, with distinct labels. Hiding a `receivedAmount` that differs from `amount` is
 * dishonest about money.
 */
export function AmountComparison({
  amount,
  receivedAmount,
  currency,
  decimalPlaces = 2,
  locale,
  requestedLabel,
  receivedLabel,
  className,
}: AmountComparisonProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <AmountDisplay
        amount={receivedAmount}
        currency={currency}
        decimalPlaces={decimalPlaces}
        locale={locale}
        size="xl"
        label={receivedLabel}
      />
      <AmountDisplay
        amount={amount}
        currency={currency}
        decimalPlaces={decimalPlaces}
        locale={locale}
        size="md"
        label={requestedLabel}
      />
    </div>
  );
}
